import { supabase } from '@/lib/supabase';

export interface TicketFilterOptions {
  status?: string;
  priority?: string;
  teamId?: string;
  assigneeId?: string;
  requesterId?: string;
  search?: string;
  sourceType?: string;
  page?: number;
  limit?: number;
}

export interface CreateTicketDTO {
  subject: string;
  description: string;
  priority?: string;
  impact?: string;
  urgency?: string;
  category?: string;
  category_id?: string;
  subcategory_id?: string;
  team_id?: string;
  assignee_id?: string;
  requester_id?: string;
  requester_email?: string;
  requester_name?: string;
  organization_id?: string;
  module?: string;
  requestType?: string;
  source?: string;
  source_type?: string;
  asset_id?: string;
  cc?: string[];
  bcc?: string[];
  custom_fields?: Record<string, any>;
  attachments?: File[];
}

export interface UpdateTicketDTO {
  subject?: string;
  description?: string;
  status?: string;
  priority?: string;
  category_id?: string;
  subcategory_id?: string;
  team_id?: string;
  assignee_id?: string;
  resolution_code?: string;
  closure_notes?: string;
  custom_fields?: Record<string, any>;
}

export interface TicketCommentDTO {
  ticket_id: string;
  content: string;
  is_internal?: boolean;
}

export const IT_SUPPORT_TEAM_ID = '11111111-1111-1111-1111-111111111111';
export const SOC_TEAM_ID = '22222222-2222-2222-2222-222222222222';

/**
 * Supabase ticket adapter used by the frontend API layer.
 * Adheres strictly to Row Level Security and database-level validation.
 */
export class TicketService {
  /**
   * Fetch tickets conforming to user role & RLS policies
   */
  static async getTickets(filters?: TicketFilterOptions): Promise<any[]> {
    if (!supabase) throw new Error('Supabase client is not configured.');

    let query = supabase
      .from('tickets')
      .select(`
        *,
        ticket_events (*),
        ticket_comments (*, author:profiles!ticket_comments_author_id_fkey(id, full_name, email, role)),
        ticket_attachments (*),
        teams:team_id (id, name, code),
        categories:category_id (id, name),
        requester:profiles!tickets_requester_id_fkey (id, full_name, email),
        assignee:profiles!tickets_assignee_id_fkey (id, full_name, email),
        organizations:organization_id (id, organization_name)
      `)
      .order('created_at', { ascending: false });

    if (filters?.status && filters.status !== 'ALL') {
      query = query.ilike('status', filters.status);
    }
    if (filters?.priority && filters.priority !== 'ALL') {
      query = query.ilike('priority', filters.priority);
    }
    if (filters?.teamId) {
      query = query.eq('team_id', filters.teamId);
    }
    if (filters?.assigneeId) {
      query = query.eq('assignee_id', filters.assigneeId);
    }
    if (filters?.requesterId) {
      query = query.eq('requester_id', filters.requesterId);
    }
    if (filters?.sourceType) {
      query = query.eq('source_type', filters.sourceType);
    }
    if (filters?.search) {
      const term = `%${filters.search.trim()}%`;
      query = query.or(`subject.ilike.${term},description.ilike.${term},number.ilike.${term}`);
    }
    if (filters?.limit) {
      const from = ((filters.page || 1) - 1) * filters.limit;
      query = query.range(from, from + filters.limit - 1);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }

  /**
   * Get single ticket by UUID or Ticket Number (e.g. INC-1001)
   */
  static async getTicketById(idOrNumber: string): Promise<any> {
    if (!supabase) throw new Error('Supabase client is not configured.');

    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(idOrNumber);
    const filterCol = isUuid ? 'id' : 'number';

    const { data, error } = await supabase
      .from('tickets')
      .select(`
        *,
        ticket_events (*),
        ticket_comments (*, author:profiles!ticket_comments_author_id_fkey(id, full_name, email, role)),
        ticket_attachments (*),
        teams:team_id (id, name, code),
        categories:category_id (id, name),
        requester:profiles!tickets_requester_id_fkey (id, full_name, email),
        assignee:profiles!tickets_assignee_id_fkey (id, full_name, email),
        organizations:organization_id (id, organization_name)
      `)
      .eq(filterCol, idOrNumber)
      .maybeSingle();

    if (error) throw error;
    if (!data) throw new Error(`Ticket "${idOrNumber}" not found.`);
    return data;
  }

  /**
   * Create a new ticket with atomic database-generated numbering and initial audit event
   */
  static async createTicket(dto: CreateTicketDTO): Promise<any> {
    if (!supabase) throw new Error('Supabase client is not configured.');

    const { data: sessionData } = await supabase.auth.getSession();
    const currentUser = sessionData?.session?.user;
    if (!currentUser) throw new Error('Authentication required to create a ticket.');

    // 1. Resolve user profile for organization_id
    const { data: userProfile } = await supabase
      .from('profiles')
      .select('id, organization_id, role, full_name, email')
      .eq('id', currentUser.id)
      .maybeSingle();

    const organizationId = dto.organization_id || userProfile?.organization_id;
    if (!organizationId) {
      throw new Error('Organization context could not be determined.');
    }

    // 2. Generate atomic sequential ticket number via RPC or fallback
    let ticketNumber = '';
    try {
      const { data: rpcNum, error: rpcErr } = await supabase.rpc('generate_next_ticket_number', {
        p_org_id: organizationId
      });
      if (!rpcErr && rpcNum) {
        ticketNumber = rpcNum;
      }
    } catch (e) {
      console.warn('RPC generate_next_ticket_number fallback:', e);
    }

    if (!ticketNumber) {
      const year = new Date().getFullYear();
      const rand = Math.floor(1000 + Math.random() * 9000);
      ticketNumber = `INC-${year}-${rand}`;
    }

    // 3. Resolve routing (default to IT Support team for normal user requests)
    let teamId = dto.team_id || null;
    let sourceType = dto.source_type || 'USER_CREATED';
    if (!teamId) {
      teamId = sourceType === 'SENTINEL' ? SOC_TEAM_ID : IT_SUPPORT_TEAM_ID;
    }

    // 4. Insert ticket record under RLS
    const newRecord: any = {
      number: ticketNumber,
      subject: dto.subject.trim(),
      description: dto.description.trim(),
      priority: dto.priority || 'Medium',
      impact: dto.impact || 'Medium',
      urgency: dto.urgency || 'Medium',
      status: 'Open',
      category_id: dto.category_id || null,
      subcategory_id: dto.subcategory_id || null,
      team_id: teamId,
      assignee_id: dto.assignee_id || null,
      requester_id: dto.requester_id || currentUser.id,
      requester_email: dto.requester_email || userProfile?.email || currentUser.email,
      organization_id: organizationId,
      module: dto.module || 'Incident',
      source: dto.source || 'Web',
      source_type: sourceType,
      custom: dto.custom_fields || {}
    };

    const { data: createdTicket, error: insertError } = await supabase
      .from('tickets')
      .insert(newRecord)
      .select()
      .single();

    if (insertError) throw insertError;

    // 5. Create initial lifecycle event in ticket_events
    await supabase.from('ticket_events').insert({
      ticket_id: createdTicket.id,
      kind: 'created',
      label: `Ticket created with ID ${ticketNumber}`,
      actor: userProfile?.full_name || currentUser.email || 'User'
    }).then(() => { });

    // 6. Handle attachments if supplied
    if (dto.attachments && dto.attachments.length > 0) {
      for (const file of dto.attachments) {
        await TicketService.uploadAttachment(createdTicket.id, file).catch(e => {
          console.warn(`Attachment upload failed for ${file.name}:`, e);
        });
      }
    }

    return createdTicket;
  }

  /**
   * Update existing ticket fields
   */
  static async updateTicket(id: string, dto: UpdateTicketDTO): Promise<any> {
    if (!supabase) throw new Error('Supabase client is not configured.');

    const updates: any = {
      updated_at: new Date().toISOString()
    };

    if (dto.subject !== undefined) updates.subject = dto.subject;
    if (dto.description !== undefined) updates.description = dto.description;
    if (dto.priority !== undefined) updates.priority = dto.priority;
    if (dto.category_id !== undefined) updates.category_id = dto.category_id;
    if (dto.subcategory_id !== undefined) updates.subcategory_id = dto.subcategory_id;
    if (dto.team_id !== undefined) updates.team_id = dto.team_id;
    if (dto.assignee_id !== undefined) updates.assignee_id = dto.assignee_id;
    if (dto.resolution_code !== undefined) updates.resolution_code = dto.resolution_code;
    if (dto.closure_notes !== undefined) updates.closure_notes = dto.closure_notes;
    if (dto.custom_fields !== undefined) updates.custom = dto.custom_fields;

    const { data, error } = await supabase
      .from('tickets')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Update ticket status (Workflow transition)
   */
  static async updateStatus(ticketId: string, status: string, comment?: string, isInternal = false): Promise<any> {
    if (!supabase) throw new Error('Supabase client is not configured.');

    // Try RPC workflow procedure first
    try {
      const { data: rpcRes, error: rpcErr } = await supabase.rpc('update_ticket_status_workflow', {
        p_ticket_id: ticketId,
        p_new_status: status,
        p_comment: comment || null,
        p_is_internal: isInternal
      });
      if (!rpcErr && rpcRes?.success) {
        return rpcRes;
      }
    } catch (e) {
      console.warn('RPC update_ticket_status_workflow fallback:', e);
    }

    // Direct update under RLS
    const updates: any = {
      status,
      updated_at: new Date().toISOString()
    };
    if (status === 'Resolved') updates.resolved_at = new Date().toISOString();
    if (status === 'Closed') updates.closed_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('tickets')
      .update(updates)
      .eq('id', ticketId)
      .select()
      .single();

    if (error) throw error;

    // Record Event
    const { data: session } = await supabase.auth.getSession();
    const actor = session?.session?.user?.email || 'User';
    await supabase.from('ticket_events').insert({
      ticket_id: ticketId,
      kind: 'status_change',
      label: `Status changed to ${status}`,
      actor
    });

    if (comment && comment.trim()) {
      await TicketService.addComment({
        ticket_id: ticketId,
        content: comment.trim(),
        is_internal: isInternal
      });
    }

    return data;
  }

  /**
   * Assign ticket to an engineer or team
   */
  static async assignTicket(ticketId: string, assigneeId: string | null, teamId?: string | null): Promise<any> {
    if (!supabase) throw new Error('Supabase client is not configured.');

    const updates: any = {
      assignee_id: assigneeId,
      updated_at: new Date().toISOString()
    };
    if (teamId !== undefined) updates.team_id = teamId;

    const { data, error } = await supabase
      .from('tickets')
      .update(updates)
      .eq('id', ticketId)
      .select()
      .single();

    if (error) throw error;

    // Record event
    const { data: session } = await supabase.auth.getSession();
    const actor = session?.session?.user?.email || 'System';
    await supabase.from('ticket_events').insert({
      ticket_id: ticketId,
      kind: 'assignment',
      label: assigneeId ? `Assigned to technician` : `Ticket unassigned`,
      actor
    });

    return data;
  }

  /**
   * Add a comment (Public or Internal Note)
   */
  static async addComment(dto: TicketCommentDTO): Promise<any> {
    if (!supabase) throw new Error('Supabase client is not configured.');

    const { data: session } = await supabase.auth.getSession();
    const userId = session?.session?.user?.id;
    if (!userId) throw new Error('Authentication required to add comment.');

    const { data, error } = await supabase
      .from('ticket_comments')
      .insert({
        ticket_id: dto.ticket_id,
        author_id: userId,
        content: dto.content.trim(),
        is_internal: Boolean(dto.is_internal)
      })
      .select(`*, author:profiles!ticket_comments_author_id_fkey(id, full_name, email, role)`)
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Upload attachment directly to Supabase Storage bucket and link to ticket
   */
  static async uploadAttachment(ticketId: string, file: File): Promise<any> {
    if (!supabase) throw new Error('Supabase client is not configured.');

    const fileExt = file.name.split('.').pop();
    const fileName = `${ticketId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `tickets/${fileName}`;

    // Upload to 'attachments' bucket in Supabase Storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('attachments')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) throw uploadError;

    // Record metadata in ticket_attachments table
    const { data: attachmentRecord, error: recordError } = await supabase
      .from('ticket_attachments')
      .insert({
        ticket_id: ticketId,
        name: file.name,
        size: file.size,
        content_type: file.type || 'application/octet-stream',
        path: uploadData.path,
        storage_url: uploadData.path
      })
      .select()
      .single();

    if (recordError) throw recordError;
    return attachmentRecord;
  }
}
